/**
 * fishing.ts — FISHING, the pure half (2026-09-24). The server rolls the fish (field_guide_catalog.rollFish on its
 * own clock and weather) and judges the timing (reelIn inside biteAt … +25 s); the client plays the REEL — a catch
 * zone you hold up against gravity while the fish darts about the bar (Stardew's fishing bar: the fish's `fight`
 * sets how fast and how often it darts; the progress meter fills while the fish is inside the zone and drains while
 * it is not). A full meter lands it; an empty one loses it.
 *
 * THE MODEL FACING (LOOKED AT, 2026-09-24, side + top renders with a +X marker): eight of the generated fish face
 * model −X, the ember guppy faces +X, the stormray faces +Z. `fishModelYaw` is the Y rotation that turns a model's
 * head onto +Z, so a fish drawn at `yaw = atan2(dx, dz) + fishModelYaw(key)` swims along (dx, dz).
 */
import type { Water } from './field_guide_catalog.js';

const HEAD_TO_Z: Record<string, number> = { ember_guppy: -Math.PI / 2, stormray: 0 };
/** The yaw that turns a fish model's head onto +Z (default: head at −X → +π/2). */
export function fishModelYaw(key: string): number { return HEAD_TO_Z[key] ?? Math.PI / 2; }

export const REEL = {
  /** Half the catch zone's height on the 0..1 bar. */
  ZONE_HALF: 0.13,
  /** Zone accelerations (bar units / s²): holding lifts it, letting go it sinks. */
  LIFT: 3.4,
  GRAVITY: 2.2,
  /** Progress per second inside / outside the zone (fight 1). */
  FILL: 0.34,
  DRAIN: 0.2,
  START: 0.32,
  /** The fish stays above this: a zone left resting on the floor is never a free catch. */
  FISH_FLOOR: 0.3,
  /** The server takes the reel within 25 s of the bite; the client gives up at this. */
  LIMIT_S: 20,
} as const;

export interface ReelState {
  zone: number; zoneV: number;
  fish: number; fishTarget: number; retargetIn: number;
  progress: number; t: number;
  done: 'landed' | 'lost' | null;
}

export function newReel(): ReelState {
  return { zone: 0.35, zoneV: 0, fish: 0.5, fishTarget: 0.5, retargetIn: 0, progress: REEL.START, t: 0, done: null };
}

/** One step of the reel. `fight` 1..4 (the catalog's); `r` a uniform source. Pure: returns a new state. */
export function stepReel(s: ReelState, holding: boolean, dt: number, fight: number, r: () => number): ReelState {
  if (s.done) return s;
  const d = Math.max(0, Math.min(0.1, dt));
  const f = Math.max(1, Math.min(4, fight));
  // The zone: a small body on a spring-less rail, bounced dead at the ends.
  let zoneV = s.zoneV + (holding ? REEL.LIFT : -REEL.GRAVITY) * d;
  let zone = s.zone + zoneV * d;
  const lo = REEL.ZONE_HALF, hi = 1 - REEL.ZONE_HALF;
  if (zone < lo) { zone = lo; zoneV = 0; }
  if (zone > hi) { zone = hi; zoneV = 0; }
  // The fish: darts to a new target every so often; a fighter darts farther and sooner.
  let retargetIn = s.retargetIn - d;
  let fishTarget = s.fishTarget;
  if (retargetIn <= 0) {
    const reach = 0.18 + 0.12 * f;
    fishTarget = Math.max(REEL.FISH_FLOOR, Math.min(0.94, s.fish + (r() * 2 - 1) * reach));
    retargetIn = (1.5 - 0.22 * f) * (0.6 + r() * 0.8);
  }
  const speed = 0.7 + 0.45 * f;
  const fish = s.fish + Math.max(-speed * d, Math.min(speed * d, fishTarget - s.fish));
  // The meter.
  const inside = Math.abs(fish - zone) <= REEL.ZONE_HALF;
  const fill = REEL.FILL * (1 - 0.13 * (f - 1));
  const drain = REEL.DRAIN * (0.8 + 0.2 * f);
  const progress = Math.max(0, Math.min(1, s.progress + (inside ? fill : -drain) * d));
  const t = s.t + d;
  const done = progress >= 1 ? 'landed' : progress <= 0 || t >= REEL.LIMIT_S ? 'lost' : null;
  return { zone, zoneV, fish, fishTarget, retargetIn, progress, t, done };
}

export interface CastSpot { x: number; z: number; water: Water; dist: number }

/**
 * The water a cast would land in: the first sample along the facing (three.js forward = (−sin yaw, −cos yaw)) from
 * `minM` to `maxM` whose water kind is known — the shore you face, not the sea behind you.
 */
export function castSearch(px: number, pz: number, yaw: number, kindAt: (x: number, z: number) => Water | null, minM = 4, maxM = 20, step = 1.5): CastSpot | null {
  const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
  for (let d = minM; d <= maxM + 1e-6; d += step) {
    const x = px + fx * d, z = pz + fz * d;
    const w = kindAt(x, z);
    if (w) {
      // Land the bobber a little past the waterline (2 m, never past the reach).
      const land = Math.min(maxM, d + 2);
      const lx = px + fx * land, lz = pz + fz * land;
      const w2 = kindAt(lx, lz) ?? w;
      return { x: lx, z: lz, water: w2, dist: land };
    }
  }
  return null;
}

/** A caught fish's length (cm) → the model's drawn length (m); a guppy is a finger, a ray a doorway. */
export function fishDrawLength(cm: number): number { return Math.max(0.08, Math.min(2.2, cm / 100)); }
